import { createAction, props } from '@ngrx/store';
import {
  BdnResponse,
  BioBlend,
  BlendResponse,
  BlendsResponse,
  CiiCfResponse,
  DebugLog,
  EDN,
  ReportFormInterface,
  ReportType,
  Ship,
  ShipReportingRequirements,
} from 'src/types';

export const enum ReportingActions {
  LoadReports = '[REPORTING] Load Reports',
  LoadReportsSuccess = '[REPORTING] Load Reports Success',
  LoadReportsFailure = '[REPORTING] Load Reports Failure',
  RouteToLastShip = '[REPORTING] Route To Last Ship',
  RouteToNewReport = '[REPORTING] Route To New Report',
  RouteToReport = '[REPORTING] Route To Report',
  SetSelectedReport = '[REPORTING] Set Selected Report',
  LoadShip = '[REPORTING] Load Ship',
  LoadShipSuccess = '[REPORTING] Load Ship Success',
  LoadShipFailure = '[REPORTING] Load Ship Failure',
  LoadBlends = '[REPORTING] Load Blends',
  LoadBlendsSuccess = '[REPORTING] Load Blends Success',
  LoadBlendsFailure = '[REPORTING] Load Blends Failure',
  LoadShipOfflineFailure = '[REPORTING] Load Ship Offline Failure',
  ClearShip = '[REPORTING] Clear Ship',
  CreateReport = '[REPORTING] Create Report',
  CreateReportFailure = '[REPORTING] Create Report Failure',
  UpdateReport = '[REPORTING] Update Report',
  UpdateReportFailure = '[REPORTING] Update Report Failure',
  UpdateCurrentReport = '[REPORTING] Update Current Report',
  SyncReport = '[REPORTING] Sync Report',
  SyncReportNoop = '[REPORTING] Sync Report Noop',
  SyncReportSuccess = '[REPORTING] Sync Report Success',
  SyncReportFailure = '[REPORTING] Sync Report Failure',
  SyncReportAsNew = '[REPORTING] Sync Report As New',
  SyncReportWarning = '[REPORTING] Sync Report Warning',
  SyncReportFinalized = '[REPORTING] Sync Report Finalized',
  SetReportState = '[REPORTING] Set Report State',
  SetShipReportingRequirements = '[REPORTING] Set Ship Reporting Requirements',
  SetBioBlends = '[REPORTING] Set Bio Blends',
  SaveSelectedBunkersLocally = '[REPORTING] Save Selected Bunkers Locally',
  SetSelectedBunkerTypes = '[REPORTING] Set Selected Bunker Types',
  LoadSelectedBunkers = '[REPORTING] Load Selected Bunkers',
  SaveTimezoneLocally = '[REPORTING] Save Timezone Locally',
  SetTimezone = '[REPORTING] Set Timezone',
  LoadTimezone = '[REPORTING] Load Timezone',
  SaveCpidLocally = '[REPORTING] Save Ship Uuid Locally',
  SetCpid = '[REPORTING] Set Ship Uuid',
  LoadCpid = '[REPORTING] Load Ship Uuid',
  SetDebugLog = '[REPORTING] Set Debug Log',
  HydrateStore = '[REPORTING] Hydrate Store',
}

/** Routing */
export const routeToLastShip = createAction(ReportingActions.RouteToLastShip, props<{ uuid: string }>());
export const routeToNewReport = createAction(ReportingActions.RouteToNewReport, props<{ reportType: string }>());
export const routeToReport = createAction(
  ReportingActions.RouteToReport,
  props<{ localId: string; reportType?: ReportType }>()
);

/** Selected Report */
export const setSelectedReport = createAction(
  ReportingActions.SetSelectedReport,
  props<{
    localId: string;
    reportType?: ReportType;
    reportDate?: Date;
    localTime?: string;
    timezone?: string;
  }>()
);

/** Load Reports */
export const loadReports = createAction(ReportingActions.LoadReports, props<{ imo: number }>());
export const loadReportsSuccess = createAction(
  ReportingActions.LoadReportsSuccess,
  props<{ reports: ReportFormInterface[] }>()
);
export const loadReportsFailure = createAction(ReportingActions.LoadReportsFailure, props<{ error: unknown }>());

/** Load Ship */
export const loadShip = createAction(
  ReportingActions.LoadShip,
  props<{ shipParameter: string | null }>()
);
export const loadShipSuccess = createAction(
  ReportingActions.LoadShipSuccess,
  props<{ ship: Ship; requirements: ShipReportingRequirements }>()
);
export const loadShipOfflineFailure = createAction(ReportingActions.LoadShipOfflineFailure);
export const loadShipFailure = createAction(ReportingActions.LoadShipFailure, props<{ error: unknown }>());

/** Load Blend */
export const loadBlends = createAction(ReportingActions.LoadBlends, props<{ uuid: string }>());
export const loadBlendsSuccess = createAction(
  ReportingActions.LoadBlendsSuccess,
  props<{ blends: BlendsResponse[] }>()
);
export const loadBlendsFailure = createAction(ReportingActions.LoadBlendsFailure, props<{ error: unknown }>());
export const setBioBlends = createAction(ReportingActions.SetBioBlends, props<{ bioBlends: BioBlend[] }>());

/** Clear Ship */
export const clearShip = createAction(ReportingActions.ClearShip, props<{ imo: number; external?: boolean }>());

/** Create Report */
export const createReport = createAction(ReportingActions.CreateReport, props<{ report: ReportFormInterface }>());
export const createReportFailure = createAction(ReportingActions.CreateReportFailure, props<{ error: unknown }>());

/** Update Report */
export const updateReport = createAction(
  ReportingActions.UpdateReport,
  props<{ report: ReportFormInterface; imo: number; id: number | string }>()
);
export const updateReportFailure = createAction(ReportingActions.UpdateReportFailure, props<{ error: unknown }>());

/** Update Current Report */
export const updateCurrentReport = createAction(
  ReportingActions.UpdateCurrentReport,
  props<{ report: ReportFormInterface }>()
);

/** Sync Report */
export const syncReport = createAction(ReportingActions.SyncReport, props<{ report: ReportFormInterface }>());
export const syncReportNoop = createAction(ReportingActions.SyncReportNoop);
export const syncReportSuccess = createAction(
  ReportingActions.SyncReportSuccess,
  props<{
    report: ReportFormInterface;
    previousVersion?: string;
    imo: number;
    bunkers: {
      grade: string;
      id: number;
      bdn?: BdnResponse;
      blend?: BlendResponse;
      ciiCorrectionFactor?: CiiCfResponse;
    }[];
    status: string;
    edns: EDN[];
  }>()
);
export const syncReportAsNew = createAction(
  ReportingActions.SyncReportAsNew,
  props<{ report: ReportFormInterface; imo: number }>()
);

export const syncReportFailure = createAction(
  ReportingActions.SyncReportFailure,
  props<{
    response: { status: number; error: { error: string; status: string; errorsList: string[]; errorDetail: string; errorCode?: string; validationErrors?: string[] } };
    report: ReportFormInterface;
    imo: number;
  }>()
);
export const syncReportWarning = createAction(
  ReportingActions.SyncReportWarning,
  props<{ report: ReportFormInterface; imo: number }>()
);
export const syncReportFinalized = createAction(
  ReportingActions.SyncReportFinalized,
  props<{ localId: string }>()
);

/** Set Report State */
export const setReportState = createAction(
  ReportingActions.SetReportState,
  props<{ reports: ReportFormInterface[] }>()
);
export const setShipReportingRequirements = createAction(
  ReportingActions.SetShipReportingRequirements,
  props<{ requirements: ShipReportingRequirements }>()
);

/** Selected bunkers */
export const saveSelectedBunkersLocally = createAction(
  ReportingActions.SaveSelectedBunkersLocally,
  props<{ selectedBunkers: string[] }>()
);
export const setSelectedBunkerTypes = createAction(
  ReportingActions.SetSelectedBunkerTypes,
  props<{ selectedBunkerTypes: string[] }>()
);
export const loadSelectedBunkers = createAction(ReportingActions.LoadSelectedBunkers);

// Timezone actions
export const saveTimezoneLocally = createAction(ReportingActions.SaveTimezoneLocally, props<{ timezone: string }>());
export const setTimezone = createAction(ReportingActions.SetTimezone, props<{ timezone: string }>());

// CPID actions
export const saveCpidLocally = createAction(ReportingActions.SaveCpidLocally, props<{ cpid: string }>());
export const setCpid = createAction(ReportingActions.SetCpid, props<{ cpid: string }>());

/** Set debug log */
export const setDebugLog = createAction(ReportingActions.SetDebugLog, props<{ log: DebugLog }>());

export const hydrateStore = createAction(
  ReportingActions.HydrateStore,
  props<{ currentReport: ReportFormInterface, selectedBunkerTypes: string[] }>()
);
